/**
 * Web AuthClient: Better Auth on /auth/* (same origin — bundle-web forwards it to the API, Vite proxies it in dev).
 * The session lives in Better Auth's HttpOnly, first-party cookie; this module never sees the session token.
 * View models get 15-minute access tokens from GET /auth/token.
 */
import { AccountSecurityLogic, ApiFailure, type ApiErrorKind, type AuthClient } from "@bundle/core";
import { API_BASE } from "./env.ts";

function kindFor(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 429) return "rateLimited";
  if (status >= 400 && status < 500) return "invalid";
  return "server";
}

const AUTH_TIMEOUT_MS = 20_000;

/** A Better Auth call on /auth/* with the session cookie (also used by account linking, platform/linking.ts). */
export async function authCall<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
  // A request that never answers (a stalled connection after sleep or a network change) fails instead of holding
  // "Loading your workspaces…" forever.
  const init: RequestInit = { method, credentials: "include", headers: { accept: "application/json" }, signal: AbortSignal.timeout(AUTH_TIMEOUT_MS) };
  if (body !== undefined) {
    init.headers = { accept: "application/json", "content-type": "application/json" };
    init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth${path}`, init);
  } catch (e) {
    throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
  }
  const text = await res.text();
  let json: unknown;
  try {
    json = text === "" ? undefined : JSON.parse(text);
  } catch {
    json = undefined;
  }
  if (!res.ok) {
    const err = json as { code?: string; message?: string } | undefined;
    const kind = res.status === 502 || res.status === 504 ? "network" : kindFor(res.status);
    throw new ApiFailure(kind, res.status, err?.code ?? String(res.status), err?.message ?? res.statusText);
  }
  return json as T;
}

type Provider = "google" | "apple";

/** `returnTo` plus a query marker (`withMarker("/x?a=1", "k", "v")` → "/x?a=1&k=v"). */
export function withMarker(returnTo: string, key: string, value: string): string {
  return `${returnTo}${returnTo.includes("?") ? "&" : "?"}${key}=${encodeURIComponent(value)}`;
}

/** `returnTo` plus a marker the app reads when the provider sends the user back with an error. */
function withAuthError(returnTo: string, provider: Provider): string {
  return withMarker(returnTo, "authError", provider);
}

/** `returnTo` plus a marker saying how the sign-in ended (Better Auth sends new accounts to `newUserCallbackURL`). */
function withSignedIn(returnTo: string, provider: Provider, isNew: boolean): string {
  return withMarker(returnTo, "signedIn", isNew ? `${provider}-new` : provider);
}

/** Web redirect sign-in (Google, Apple): Better Auth answers with the provider's URL and the page leaves for it. */
async function redirectSignIn(provider: Provider, returnTo: string): Promise<"redirected"> {
  const res = await authCall<{ url?: string }>("POST", "/sign-in/social", {
    provider,
    callbackURL: withSignedIn(returnTo, provider, false),
    newUserCallbackURL: withSignedIn(returnTo, provider, true),
    errorCallbackURL: withAuthError(returnTo, provider),
    disableRedirect: true,
  });
  if (typeof res.url !== "string") throw new ApiFailure("server", 500, "no_url", `${provider} sign-in did not return a URL`);
  window.location.assign(res.url);
  return "redirected";
}

/**
 * Analytics hook (sign_up / login): only the method and whether the account is new — never who. Set by the
 * container; email codes report here, Google reports through the redirect marker (takeSignInMarker).
 */
export type SignInListener = (method: "email" | Provider, isNew: boolean) => void;
let signInListener: SignInListener | undefined;

/** Better Auth creates the account on the first verified email code, so a brand-new account means sign-up. */
function isNewAccount(createdAt: unknown, now = Date.now()): boolean {
  const t = typeof createdAt === "string" || typeof createdAt === "number" ? new Date(createdAt).getTime() : Number.NaN;
  return Number.isFinite(t) && now - t < 2 * 60_000;
}

export function onSignIn(listener: SignInListener | undefined): void {
  signInListener = listener;
}

export const webAuth: AuthClient = {
  async sendEmailCode(email) {
    await authCall("POST", "/email-otp/send-verification-otp", { email, type: "sign-in" });
  },
  async verifyEmailCode(email, code, name) {
    // Create account: the typed name lands on the account this code creates (ignored for an existing one).
    const res = await authCall<{ user?: { createdAt?: unknown } } | undefined>("POST", "/sign-in/email-otp", name === undefined ? { email, otp: code } : { email, otp: code, name });
    // Two-step verification on: no session yet, the challenge cookie is set (the second step comes next).
    const step = AccountSecurityLogic.signInStepOf(res);
    if (step.step === "twoFactor") return step;
    signInListener?.("email", isNewAccount(res?.user?.createdAt));
    return step;
  },
  signInWithGoogle: (returnTo) => redirectSignIn("google", returnTo),
  // Apple's page POSTs back to /auth/callback/apple (form_post); Better Auth continues with a GET to `returnTo`.
  signInWithApple: (returnTo) => redirectSignIn("apple", returnTo),
  async accessToken() {
    const res = await authCall<{ token?: string }>("GET", "/token");
    if (typeof res.token !== "string") throw new ApiFailure("auth", 401, "no_token", "No access token");
    return res.token;
  },
  async signOut() {
    await authCall("POST", "/sign-out", {});
  },
};

/** Removes query parameters from the address bar (without a navigation). */
export function dropParams(...names: string[]): void {
  const url = new URL(location.href);
  for (const n of names) url.searchParams.delete(n);
  history.replaceState(history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

/** Reads (and removes from the address bar) the error marker of a failed provider redirect: which provider. */
export function takeOAuthError(): Provider | undefined {
  const v = new URL(location.href).searchParams.get("authError");
  if (v === null) return undefined;
  dropParams("authError", "error", "error_description");
  return v === "apple" ? "apple" : "google";
}

/**
 * Reads (and removes from the address bar) the second-step marker: a provider redirect for an account with two-step
 * verification on comes back with ?twoFactor=1 (and no session; the challenge cookie is set).
 */
export function takeTwoFactorMarker(): boolean {
  const url = new URL(location.href);
  if (url.searchParams.get("twoFactor") !== "1") return false;
  dropParams("twoFactor");
  return true;
}

/** Reads (and removes from the address bar) the marker of a completed provider sign-in. */
export function takeSignInMarker(): { method: Provider; isNew: boolean } | undefined {
  const v = new URL(location.href).searchParams.get("signedIn");
  if (v === null) return undefined;
  dropParams("signedIn");
  const m = /^(google|apple)(-new)?$/.exec(v);
  return m ? { method: m[1] as Provider, isNew: m[2] !== undefined } : undefined;
}
